/**
 * RemindersModal
 *
 * Gerencia a lista de lembretes automáticos do nó Agendamento.
 *
 * Props:
 *   show:            bool
 *   onClose:         () => void
 *   onSave:          (reminders: Reminder[]) => void
 *   initialReminders: Reminder[] | null
 *
 * Schema Reminder:
 *   {
 *     trigger: 'before_event' | 'after_creation' | 'after_event',  // default=before_event se ausente
 *     channel: 'whatsapp'|'email',
 *     minutes_before: int,         // usado quando trigger=before_event
 *     minutes_after: int,          // usado quando trigger=after_creation
 *     minutes_after_event: int,    // usado quando trigger=after_event
 *     message: string,
 *     subject?: string (só email)
 *   }
 *
 * Backward-compat: lembretes salvos antes da feature de after_creation não
 * têm a chave `trigger` — o backend trata como 'before_event'.
 */

import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import {
  IconBell,
  IconPlus,
  IconTrash,
  IconBrandWhatsapp,
  IconMail,
  IconHelpCircle,
  IconCheck,
  IconClockHour4,
  IconCalendarPlus,
  IconCalendarCheck,
} from '@tabler/icons-react';
import RemindersGuideModal from './RemindersGuideModal';
import '../../styles/RemindersModal.css';

const UNIT_TO_MINUTES = { min: 1, hora: 60, dia: 60 * 24 };

// Triggers — espelha o schema do backend (agents_flow/services/reminders.py)
const TRIGGER_BEFORE = 'before_event';
const TRIGGER_AFTER_CREATION = 'after_creation';
const TRIGGER_AFTER_EVENT = 'after_event';

const VALID_TRIGGERS = [TRIGGER_BEFORE, TRIGGER_AFTER_CREATION, TRIGGER_AFTER_EVENT];

// Lê o offset (em minutos) de um reminder, agnóstico de trigger.
// Lembretes legados não têm `trigger` — tratamos como before_event lendo `minutes_before`.
const reminderOffsetMinutes = (r) => {
  if (!r) return 0;
  if (r.trigger === TRIGGER_AFTER_CREATION) return Number(r.minutes_after) || 0;
  if (r.trigger === TRIGGER_AFTER_EVENT) return Number(r.minutes_after_event) || 0;
  return Number(r.minutes_before) || 0;
};

const reminderTrigger = (r) => {
  if (!r) return TRIGGER_BEFORE;
  return VALID_TRIGGERS.includes(r.trigger) ? r.trigger : TRIGGER_BEFORE;
};

const minutesToHuman = (minutes) => {
  if (!minutes || minutes <= 0) return { value: '', unit: 'hora' };
  if (minutes % 1440 === 0) return { value: String(minutes / 1440), unit: 'dia' };
  if (minutes % 60 === 0) return { value: String(minutes / 60), unit: 'hora' };
  return { value: String(minutes), unit: 'min' };
};

const humanToMinutes = (value, unit) => {
  const n = parseInt(value, 10);
  if (!n || n <= 0) return 0;
  return n * (UNIT_TO_MINUTES[unit] || 1);
};

const newDraftReminder = () => ({
  trigger: TRIGGER_BEFORE,
  channel: 'whatsapp',
  minutes_before: 60,
  message: '',
  subject: '',
});

const RemindersModal = ({ show, onClose, onSave, initialReminders }) => {
  const [reminders, setReminders] = useState([]);
  const [draft, setDraft] = useState(null); // null = sem form aberto
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (show) {
      setReminders(Array.isArray(initialReminders) ? initialReminders : []);
      setDraft(null);
    }
  }, [show, initialReminders]);

  const addReminder = () => {
    if (!draft) return;
    const offsetMinutes = humanToMinutes(draft._timeValue, draft._timeUnit);
    if (offsetMinutes <= 0) return;
    if (!draft.message.trim()) return;
    if (draft.channel === 'email' && !draft.subject.trim()) return;
    const trigger = VALID_TRIGGERS.includes(draft.trigger) ? draft.trigger : TRIGGER_BEFORE;
    // Salva APENAS o offset relevante para o trigger ativo — evita ambiguidade
    // no payload (ex: trigger=after_creation com minutes_before=60 também
    // confundiria o backend).
    const item = {
      trigger,
      channel: draft.channel,
      message: draft.message.trim(),
    };
    if (trigger === TRIGGER_AFTER_CREATION) {
      item.minutes_after = offsetMinutes;
    } else if (trigger === TRIGGER_AFTER_EVENT) {
      item.minutes_after_event = offsetMinutes;
    } else {
      item.minutes_before = offsetMinutes;
    }
    if (draft.channel === 'email') item.subject = draft.subject.trim();
    setReminders((prev) => [...prev, item]);
    setDraft(null);
  };

  const removeReminder = (idx) => {
    setReminders((prev) => prev.filter((_, i) => i !== idx));
  };

  const openNewDraft = () => {
    const h = minutesToHuman(60);
    setDraft({ ...newDraftReminder(), _timeValue: h.value, _timeUnit: h.unit });
  };

  const updateDraft = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    onSave(reminders);
    onClose();
  };

  const renderReminderRow = (r, idx) => {
    const trigger = reminderTrigger(r);
    const h = minutesToHuman(reminderOffsetMinutes(r));
    const unitLabel = h.unit === 'min' ? 'min' : h.unit + (parseInt(h.value, 10) > 1 ? 's' : '');
    let timeLabel;
    let TimeIcon;
    if (trigger === TRIGGER_AFTER_CREATION) {
      timeLabel = `${h.value} ${unitLabel} após criação`;
      TimeIcon = IconCalendarPlus;
    } else if (trigger === TRIGGER_AFTER_EVENT) {
      timeLabel = `${h.value} ${unitLabel} após atendimento`;
      TimeIcon = IconCalendarCheck;
    } else {
      timeLabel = `${h.value} ${unitLabel} antes`;
      TimeIcon = IconClockHour4;
    }
    return (
      <div key={idx} className="rem-row">
        <div className="rem-row__channel">
          {r.channel === 'whatsapp' ? (
            <IconBrandWhatsapp size={16} style={{ color: '#10b981' }} />
          ) : (
            <IconMail size={16} style={{ color: '#3b82f6' }} />
          )}
        </div>
        <div className="rem-row__time">
          <TimeIcon size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          {timeLabel}
        </div>
        <div className="rem-row__msg">
          {r.subject && <div className="rem-row__subject">📧 {r.subject}</div>}
          <div className="rem-row__body">{r.message}</div>
        </div>
        <button
          type="button"
          onClick={() => removeReminder(idx)}
          className="rem-icon-btn rem-icon-btn--danger"
          title="Remover lembrete"
        >
          <IconTrash size={14} />
        </button>
      </div>
    );
  };

  const canConfirmDraft = draft
    && humanToMinutes(draft._timeValue, draft._timeUnit) > 0
    && draft.message.trim()
    && (draft.channel !== 'email' || draft.subject.trim());

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="lg" className="rem-modal">
        <Modal.Header closeButton className="rem-modal__header">
          <Modal.Title className="rem-modal__title">
            <div className="rem-modal__title-icon">
              <IconBell size={20} />
            </div>
            Lembretes automáticos
            <button
              type="button"
              onClick={() => setShowGuide(true)}
              className="rem-guide-btn"
              title="Como funcionam os lembretes"
            >
              <IconHelpCircle size={14} /> Guia
            </button>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="rem-modal__body">
          {reminders.length === 0 && !draft && (
            <p className="rem-empty">
              Nenhum lembrete configurado. Você pode disparar avisos <strong>antes do evento</strong>
              {' '}(ex: lembrete 1h antes da consulta), <strong>após a criação do agendamento</strong>
              {' '}(ex: dicas pré-consulta enviadas 1h depois do agendamento criado) e{' '}
              <strong>após o atendimento</strong> (ex: pesquisa de satisfação 2h depois do término).
              Clique em <strong>Adicionar lembrete</strong> para começar.
            </p>
          )}

          {reminders.map(renderReminderRow)}

          {draft && (
            <div className="rem-draft">
              <div className="rem-draft__header">
                <span>Novo lembrete</span>
              </div>

              <div className="rem-draft__row">
                <label className="rem-draft__label">Quando enviar</label>
                <div className="rem-draft__channel-group">
                  <label className={`rem-channel-pill ${draft.trigger === TRIGGER_BEFORE ? 'rem-channel-pill--active' : ''}`}>
                    <input
                      type="radio"
                      name="rem-trigger"
                      value={TRIGGER_BEFORE}
                      checked={draft.trigger === TRIGGER_BEFORE}
                      onChange={() => updateDraft('trigger', TRIGGER_BEFORE)}
                    />
                    <IconClockHour4 size={14} /> Antes do evento
                  </label>
                  <label className={`rem-channel-pill ${draft.trigger === TRIGGER_AFTER_CREATION ? 'rem-channel-pill--active' : ''}`}>
                    <input
                      type="radio"
                      name="rem-trigger"
                      value={TRIGGER_AFTER_CREATION}
                      checked={draft.trigger === TRIGGER_AFTER_CREATION}
                      onChange={() => updateDraft('trigger', TRIGGER_AFTER_CREATION)}
                    />
                    <IconCalendarPlus size={14} /> Após criação
                  </label>
                  <label className={`rem-channel-pill ${draft.trigger === TRIGGER_AFTER_EVENT ? 'rem-channel-pill--active' : ''}`}>
                    <input
                      type="radio"
                      name="rem-trigger"
                      value={TRIGGER_AFTER_EVENT}
                      checked={draft.trigger === TRIGGER_AFTER_EVENT}
                      onChange={() => updateDraft('trigger', TRIGGER_AFTER_EVENT)}
                    />
                    <IconCalendarCheck size={14} /> Após o atendimento
                  </label>
                </div>
              </div>

              <div className="rem-draft__row">
                <label className="rem-draft__label">Canal</label>
                <div className="rem-draft__channel-group">
                  <label className={`rem-channel-pill ${draft.channel === 'whatsapp' ? 'rem-channel-pill--active' : ''}`}>
                    <input
                      type="radio"
                      name="rem-channel"
                      value="whatsapp"
                      checked={draft.channel === 'whatsapp'}
                      onChange={() => updateDraft('channel', 'whatsapp')}
                    />
                    <IconBrandWhatsapp size={14} /> WhatsApp
                  </label>
                  {/* <label className={`rem-channel-pill ${draft.channel === 'email' ? 'rem-channel-pill--active' : ''}`}>
                    <input
                      type="radio"
                      name="rem-channel"
                      value="email"
                      checked={draft.channel === 'email'}
                      onChange={() => updateDraft('channel', 'email')}
                    />
                    <IconMail size={14} /> E-mail
                  </label> */}
                </div>
              </div>

              <div className="rem-draft__row">
                <label className="rem-draft__label">
                  {draft.trigger === TRIGGER_AFTER_CREATION && 'Tempo após criação'}
                  {draft.trigger === TRIGGER_AFTER_EVENT && 'Tempo após o atendimento'}
                  {draft.trigger === TRIGGER_BEFORE && 'Antecipação'}
                </label>
                <div className="rem-draft__time">
                  <input
                    type="number"
                    min="1"
                    value={draft._timeValue}
                    onChange={(e) => updateDraft('_timeValue', e.target.value)}
                    className="rem-mini-input"
                  />
                  <select
                    value={draft._timeUnit}
                    onChange={(e) => updateDraft('_timeUnit', e.target.value)}
                    className="rem-select"
                  >
                    {draft.trigger === TRIGGER_AFTER_CREATION && (
                      <>
                        <option value="min">minuto(s) após criação</option>
                        <option value="hora">hora(s) após criação</option>
                        <option value="dia">dia(s) após criação</option>
                      </>
                    )}
                    {draft.trigger === TRIGGER_AFTER_EVENT && (
                      <>
                        <option value="min">minuto(s) após o atendimento</option>
                        <option value="hora">hora(s) após o atendimento</option>
                        <option value="dia">dia(s) após o atendimento</option>
                      </>
                    )}
                    {draft.trigger === TRIGGER_BEFORE && (
                      <>
                        <option value="min">minuto(s) antes</option>
                        <option value="hora">hora(s) antes</option>
                        <option value="dia">dia(s) antes</option>
                      </>
                    )}
                  </select>
                </div>
                {draft.trigger === TRIGGER_AFTER_CREATION && (
                  <p className="rem-hint" style={{ marginTop: 6 }}>
                    O lembrete é enviado a partir do momento em que o agendamento for criado.
                    Se o offset cair depois da data do evento, o lembrete é descartado automaticamente.
                  </p>
                )}
                {draft.trigger === TRIGGER_AFTER_EVENT && (
                  <p className="rem-hint" style={{ marginTop: 6 }}>
                    O lembrete é enviado X tempo depois do término do atendimento.
                    Útil para pesquisas de satisfação (NPS), pedido de avaliação ou
                    follow-up. Se o agendamento for cancelado ou marcado como{' '}
                    <em>faltou</em>, o lembrete é cancelado automaticamente.
                  </p>
                )}
              </div>

              {draft.channel === 'email' && (
                <div className="rem-draft__row">
                  <label className="rem-draft__label">Assunto</label>
                  <input
                    type="text"
                    value={draft.subject}
                    onChange={(e) => updateDraft('subject', e.target.value)}
                    placeholder="Ex: Lembrete: {{servico}} amanhã às {{horario}}"
                    className="rem-mini-input rem-draft__subject"
                  />
                </div>
              )}

              <div className="rem-draft__row">
                <label className="rem-draft__label">Mensagem</label>
                <textarea
                  value={draft.message}
                  onChange={(e) => updateDraft('message', e.target.value)}
                  placeholder="Olá {{nome}}! Lembrete do seu {{servico}} dia {{data}} às {{horario}} com {{profissional}}."
                  rows={3}
                  className="rem-textarea"
                />
                <p className="rem-hint">
                  Use <code>{'{{nome}}'}</code>, <code>{'{{data}}'}</code>, <code>{'{{horario}}'}</code>,{' '}
                  <code>{'{{servico}}'}</code>, <code>{'{{profissional}}'}</code>.
                </p>
              </div>

              <div className="rem-draft__actions">
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="rem-btn rem-btn--ghost"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={addReminder}
                  disabled={!canConfirmDraft}
                  className={`rem-btn rem-btn--primary ${!canConfirmDraft ? 'rem-btn--disabled' : ''}`}
                >
                  <IconCheck size={14} /> Adicionar
                </button>
              </div>
            </div>
          )}

          {!draft && (
            <button
              type="button"
              onClick={openNewDraft}
              className="rem-btn rem-btn--add"
            >
              <IconPlus size={14} /> Adicionar lembrete
            </button>
          )}
        </Modal.Body>

        <Modal.Footer className="rem-modal__footer">
          <button type="button" onClick={onClose} className="rem-btn rem-btn--ghost">
            Cancelar
          </button>
          <button type="button" onClick={handleSave} className="rem-btn rem-btn--primary">
            <IconCheck size={14} /> Salvar
          </button>
        </Modal.Footer>
      </Modal>

      <RemindersGuideModal show={showGuide} onHide={() => setShowGuide(false)} />
    </>
  );
};

export default RemindersModal;
